import { cellValidationErrors } from '@global/constants'
import { ColumnDataType } from '../types'
import { t, msg } from '@lingui/core/macro'
import { MessageDescriptor } from '@lingui/core'

interface ComparisonOperatorConfigProps {
  operator: string
  nullary: boolean
  unaryColumn: boolean
  unaryValue: boolean
  binary: boolean
  multiValued: boolean
  group: string
  description: MessageDescriptor
  needsValue?: boolean
  data?: unknown
  dataLabel?: MessageDescriptor
  noneOption?: boolean
  noneOptionLabel?: MessageDescriptor
  [key: string]: unknown
}

interface IComparisonOperatorConfig {
  operator: string
  nullary: boolean
  unaryColumn: boolean
  unaryValue: boolean
  binary: boolean
  group: string
  description: MessageDescriptor
  multiValued: boolean
  [key: string]: unknown
}

export class ComparisonOperatorConfig implements IComparisonOperatorConfig {
  operator: string

  nullary: boolean

  unaryColumn: boolean

  unaryValue: boolean

  binary: boolean

  group: string

  description: MessageDescriptor

  multiValued: boolean;

  [key: string]: unknown

  constructor(config: ComparisonOperatorConfigProps) {
    const missingProps = ComparisonOperatorConfig.validateProps(config)
    if (missingProps.length) {
      const missingPropsString = missingProps.join(', ')
      throw new Error(t`Missing required config props: ${missingPropsString}`)
    }

    // Required props
    const {
      operator,
      nullary,
      unaryColumn,
      unaryValue,
      binary,
      multiValued,
      group,
      description,
      ...optionalProps
    } = config
    this.operator = operator
    this.nullary = nullary
    this.unaryColumn = unaryColumn
    this.unaryValue = unaryValue
    this.binary = binary
    this.group = group
    this.description = description
    this.multiValued = multiValued

    // Optional props
    Object.keys(optionalProps).forEach(key => {
      this[key] = optionalProps[key]
    })
  }

  static validateProps(config: ComparisonOperatorConfigProps): string[] {
    return [
      'operator',
      'nullary',
      'unaryColumn',
      'unaryValue',
      'binary',
      'group',
      'description',
    ].reduce((memo: string[], prop) => {
      if (!Object.hasOwnProperty.call(config, prop)) {
        memo.push(prop)
      }
      return memo
    }, [])
  }

  canHaveColumnOperand() {
    return this.binary || this.unaryColumn
  }

  canHaveValueOperand() {
    return this.binary || this.unaryValue
  }

  canHaveMultipleValues() {
    return this.multiValued
  }

  supportedInQuickFilters() {
    return this.canHaveColumnOperand() && !this.canHaveMultipleValues()
  }
}

export enum Operator {
  ERRORS = '_errors',
  NOT_ERRORS = '_nerrors',
  EMPTY = '_empty',
  NOT_EMPTY = '_nempty',
  EQUALS = '_eq',
  NOT_EQUALS = '_neq',
  CONTAINS = '_contains',
  NOT_CONTAINS = '_ncontains',
  STARTS_WITH = '_starts',
  ENDS_WITH = '_ends',
  IN = '_in',
  NOT_IN = '_nin',
  GREATER_THAN = '_gt',
  LESS_THAN = '_lt',
}

export enum DateOperator {
  EMPTY = '_empty',
  NOT_EMPTY = '_nempty',
  GREATER_THAN = '_gt',
  LESS_THAN = '_lt',
  CONTAINS = '_contains',
  NOT_CONTAINS = '_ncontains',
}

export const { ERRORS } = Operator
export const { NOT_ERRORS } = Operator
export const { EMPTY } = Operator
export const { NOT_EMPTY } = Operator
export const { EQUALS } = Operator
export const { NOT_EQUALS } = Operator
export const { CONTAINS } = Operator
export const { NOT_CONTAINS } = Operator
export const { STARTS_WITH } = Operator
export const { ENDS_WITH } = Operator
export const { IN } = Operator
export const { NOT_IN } = Operator
export const { GREATER_THAN } = Operator
export const { LESS_THAN } = Operator

export const comparisonOperators = Object.freeze({
  ERRORS,
  NOT_ERRORS,
  EMPTY,
  NOT_EMPTY,
  EQUALS,
  NOT_EQUALS,
  CONTAINS,
  NOT_CONTAINS,
  STARTS_WITH,
  ENDS_WITH,
  IN,
  NOT_IN,
  GREATER_THAN,
  LESS_THAN,
})

const textTranslations = {
  EMPTY: msg`Empty`,
  NOT_EMPTY: msg`Is not empty`,
  EQUALS: msg`Equals`,
  NOT_EQUALS: msg`Does not equal`,
  IN: msg`In`,
  NOT_IN: msg`Not In`,
  ERRORS: msg`Has errors`,
  NOT_ERRORS: msg`Has no errors`,
  CONTAINS: msg`Contains`,
  NOT_CONTAINS: msg`Does not contain`,
  STARTS_WITH: msg`Starts with`,
  ENDS_WITH: msg`Ends with`,
  DATE_EMPTY: msg`Is same day`,
  DATE_NOT_EMPTY: msg`Is not day`,
  DATE_GREATER_THAN: msg`Is same or after`,
  DATE_LESS_THAN: msg`Is same or before`,
  DATA_LABEL: msg`Error Type`,
  NONE_OPTION: msg`Any error`,
}

const emptyOperatorConfig = new ComparisonOperatorConfig({
  operator: EMPTY,
  nullary: false,
  unaryColumn: true,
  unaryValue: false,
  binary: false,
  multiValued: false,
  group: 'EMPTY',
  description: textTranslations.EMPTY,
  needsValue: false,
})

const notEmptyOperatorConfig = new ComparisonOperatorConfig({
  operator: NOT_EMPTY,
  nullary: false,
  unaryColumn: true,
  unaryValue: false,
  binary: false,
  multiValued: false,
  group: 'EMPTY',
  description: textTranslations.NOT_EMPTY,
  needsValue: false,
})

const equalsOperatorConfig = new ComparisonOperatorConfig({
  operator: EQUALS,
  nullary: false,
  unaryColumn: false,
  unaryValue: false,
  binary: true,
  multiValued: false,
  group: 'EQUALS',
  description: textTranslations.EQUALS,
  needsValue: true,
})

const notEqualsOperatorConfig = new ComparisonOperatorConfig({
  operator: NOT_EQUALS,
  nullary: false,
  unaryColumn: false,
  unaryValue: false,
  binary: true,
  multiValued: false,
  group: 'EQUALS',
  description: textTranslations.NOT_EQUALS,
  needsValue: true,
})

const inOperatorConfig = new ComparisonOperatorConfig({
  operator: IN,
  nullary: false,
  unaryColumn: false,
  unaryValue: false,
  binary: true,
  multiValued: true,
  group: 'IN',
  description: textTranslations.IN,
  needsValue: true,
})

const notInOperatorConfig = new ComparisonOperatorConfig({
  operator: NOT_IN,
  nullary: false,
  unaryColumn: false,
  unaryValue: false,
  binary: true,
  multiValued: true,
  group: 'IN',
  description: textTranslations.NOT_IN,
  needsValue: true,
})

export const comparisonOperatorConfigs = Object.freeze({
  [ERRORS]: new ComparisonOperatorConfig({
    operator: ERRORS,
    nullary: true,
    unaryColumn: true,
    unaryValue: true,
    binary: true,
    multiValued: false,
    group: 'ERRORS',
    description: textTranslations.ERRORS,
    data: cellValidationErrors,
    dataLabel: textTranslations.DATA_LABEL,
    noneOption: true,
    noneOptionLabel: textTranslations.NONE_OPTION,
    needsValue: false,
  }),
  [NOT_ERRORS]: new ComparisonOperatorConfig({
    operator: NOT_ERRORS,
    nullary: true,
    unaryColumn: false, // Currently not supported
    unaryValue: false, // Currently not supported
    binary: false, // Currently not supported
    multiValued: false,
    group: 'ERRORS',
    description: textTranslations.NOT_ERRORS,
    needsValue: false,
    // feedback indicated that this was confusing, so removing for now
    // data: cellValidationErrors,
    // dataLabel: textTranslations.DATA_LABEL,
    // noneOption: true,
    // noneOptionLabel: textTranslations.NONE_OPTION,
  }),
  [EMPTY]: emptyOperatorConfig,
  [NOT_EMPTY]: notEmptyOperatorConfig,
  [EQUALS]: equalsOperatorConfig,
  [NOT_EQUALS]: notEqualsOperatorConfig,
  [CONTAINS]: new ComparisonOperatorConfig({
    operator: CONTAINS,
    nullary: false,
    unaryColumn: false,
    unaryValue: false,
    binary: true,
    multiValued: false,
    group: 'CONTAINS',
    description: textTranslations.CONTAINS,
    needsValue: true,
  }),
  [NOT_CONTAINS]: new ComparisonOperatorConfig({
    operator: NOT_CONTAINS,
    nullary: false,
    unaryColumn: false,
    unaryValue: false,
    binary: true,
    multiValued: false,
    group: 'CONTAINS',
    description: textTranslations.NOT_CONTAINS,
    needsValue: true,
  }),
  [STARTS_WITH]: new ComparisonOperatorConfig({
    operator: STARTS_WITH,
    nullary: false,
    unaryColumn: false,
    unaryValue: true,
    binary: true,
    multiValued: false,
    group: 'STARTS_ENDS',
    description: textTranslations.STARTS_WITH,
    needsValue: true,
  }),
  [ENDS_WITH]: new ComparisonOperatorConfig({
    operator: ENDS_WITH,
    nullary: false,
    unaryColumn: false,
    unaryValue: false,
    binary: true,
    multiValued: false,
    group: 'STARTS_ENDS',
    description: textTranslations.ENDS_WITH,
    needsValue: true,
  }),
  [IN]: inOperatorConfig,
  [NOT_IN]: notInOperatorConfig,
})

const comparisonOperatorConfigsSubmission = Object.freeze({
  [EMPTY]: emptyOperatorConfig,
  [NOT_EMPTY]: notEmptyOperatorConfig,
  [IN]: inOperatorConfig,
  [NOT_IN]: notInOperatorConfig,
})

const comparisonOperatorConfigsUser = Object.freeze({
  [EMPTY]: emptyOperatorConfig,
  [NOT_EMPTY]: notEmptyOperatorConfig,
  [EQUALS]: equalsOperatorConfig,
  [NOT_EQUALS]: notEqualsOperatorConfig,
  [IN]: inOperatorConfig,
  [NOT_IN]: notInOperatorConfig,
})

const comparisonOperatorConfigsDate = Object.freeze({
  [EMPTY]: emptyOperatorConfig,
  [NOT_EMPTY]: notEmptyOperatorConfig,
  [CONTAINS]: new ComparisonOperatorConfig({
    operator: CONTAINS,
    nullary: false,
    unaryColumn: false,
    unaryValue: false,
    binary: true,
    multiValued: false,
    group: 'DATE',
    description: textTranslations.DATE_EMPTY,
    needsValue: true,
  }),
  [NOT_CONTAINS]: new ComparisonOperatorConfig({
    operator: NOT_CONTAINS,
    nullary: false,
    unaryColumn: false,
    unaryValue: false,
    binary: true,
    multiValued: false,
    group: 'DATE',
    description: textTranslations.DATE_NOT_EMPTY,
    needsValue: true,
  }),
  [GREATER_THAN]: new ComparisonOperatorConfig({
    operator: GREATER_THAN,
    nullary: false,
    unaryColumn: false,
    unaryValue: false,
    binary: true,
    multiValued: false,
    group: 'DATE',
    description: textTranslations.DATE_GREATER_THAN,
    needsValue: true,
  }),
  [LESS_THAN]: new ComparisonOperatorConfig({
    operator: LESS_THAN,
    nullary: false,
    unaryColumn: false,
    unaryValue: false,
    binary: true,
    multiValued: false,
    group: 'DATE',
    description: textTranslations.DATE_LESS_THAN,
    needsValue: true,
  }),
})

export const comparisonOperatorConfigsByColType = {
  [ColumnDataType.USER]: comparisonOperatorConfigsUser,
  [ColumnDataType.DATE]: comparisonOperatorConfigsDate,
  [ColumnDataType.SUBMISSION]: comparisonOperatorConfigsSubmission,
  [ColumnDataType.DEFAULT]: comparisonOperatorConfigs,
}

function isBinaryOperator(operator: Operator) {
  return !!combinedOperands[operator].binary
}

function isUnaryValueOperator(operator: Operator) {
  return !!combinedOperands[operator].unaryValue
}

export function canHaveValueOperand(operator: Operator) {
  return isBinaryOperator(operator) || isUnaryValueOperator(operator)
}

export function valueRequiredOperand(operator: Operator) {
  return !!combinedOperands[operator].needsValue
}

const combinedOperands = {
  ...comparisonOperatorConfigsDate,
  ...comparisonOperatorConfigsSubmission,
  ...comparisonOperatorConfigsUser,
  ...comparisonOperatorConfigs,
}
